/**
 * Derive a "what's changing" diff row list from an update draft.
 *
 * Compares the persisted `draftBaseline` (captured at draft-creation time
 * via the write-handler's draftBaseline GET) against the values the agent
 * proposes in `payload`. Most update tools use a friendly alias in the
 * payload (e.g. `eta`) that the write-handler maps to the real entity
 * field (e.g. `estimatedArrival`); ALIAS_MAP captures the inverse so the
 * UI can locate the proposed value when keying off the baseline.
 *
 * Returns an empty list for create drafts (no draftBaseline).
 */
import type { AiDraftItem } from '@lognai/plugin-ai-core/src/types/draft-item';

export interface DraftDiffRow {
  /** Real entity field name (e.g. `estimatedArrival`). */
  key: string;
  /** Value captured at draft time. */
  from: unknown;
  /** Value the agent proposes. `undefined` when the row is baseline-only
   *  (captured but the agent isn't changing it — happens for multi-field
   *  tools that touch only a subset of the captured fields). */
  to: unknown;
  /** True when the agent is changing this field; false when the row is
   *  baseline-only (informational, no proposed change). */
  isProposed: boolean;
}

/**
 * Maps real entity field names to the friendlier names the agent uses in
 * tool payloads. Keep in sync with the `bodyFromPayload` mappings in each
 * plugin's `write-handlers.json`.
 */
const ALIAS_MAP: Record<string, string> = {
  estimatedArrival: 'eta',
  estimatedDeparture: 'etd',
  requestedDeliveryDate: 'requestedShipDate',
  expectedDeliveryDate: 'expectedDelivery',
};

export const computeDraftDiff = (item: AiDraftItem): DraftDiffRow[] => {
  if (!item.draftBaseline) return [];
  const baseline = item.draftBaseline;
  const payload = (item.payload ?? {}) as Record<string, unknown>;
  const rows: DraftDiffRow[] = [];
  for (const key of Object.keys(baseline)) {
    const from = baseline[key];
    let to: unknown = payload[key];
    let isProposed = to !== undefined;
    if (!isProposed) {
      const alias = ALIAS_MAP[key];
      if (alias && alias in payload) {
        to = payload[alias];
        isProposed = true;
      }
    }
    if (isProposed && from === to) continue;
    rows.push({ key, from, to, isProposed });
  }
  return rows;
};
